// 线稿提取
import { defaultHD, defaultBatchSize } from "../default";
import { ModuleEnum } from "../moduleEnum";
import type { LayoutReferState } from "@/types/image";
import { getModelEventTrackType, mapModelSource, gethdFix } from "../";
import { useEventTracking } from "@/hooks/useEventTracking";
import type { ModelState } from "@/types/model";
import { startModuleGenerate } from "./index";
import { useModelStore } from "@/store/model";
import { styleModelOptions } from "@/constants";
import { getModelDetail } from "holopix-api/src/server/model";
import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { getModuleLabelById } from "@xmiles/holopix-helper";

export const module = ModuleEnum["sketch"];

// 线稿提取专用模型 id
export const sketchModelId = ["production", "gray"].includes(import.meta.env.VITE_ENV) ? 529 : 312;

export const sketchParams = {
  module,
  prompt: "", // 用户输入的提示词
  layoutReferId: null as null | number, // 原始稿件图片id
  layoutRefer: null as null | LayoutReferState, // 原始稿件
  layoutReferWeight: 0.5, // 还原、精细
  hdFix: false, // 开启高清修复
  hdScale: defaultHD, // 高清修复数值
  batchSize: defaultBatchSize // 生成数量
};

// 将当前用户输入的生图参数转换成后端所需要
export async function getSketchGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  /***
   *
   * 由于历史原因问题,只要是没有模型的模块就会出现问题,取值是错误的,但是最终上传到服务端的数据是正确的,因为是写死的,
   * 所以只能在这里先调用一次接口把数据先纠正回来,到时候再说
   */

  let data = await getModelDetail({ id: sketchModelId });

  let params = {
    module: sketchParams.module,
    userId: userStore.userInfo.id,
    uniqueId: userStore.userInfo.uniqueId,
    artworkId: artworkStore.artworkState.id,
    modelList: [data],
    loraList: [{ modelId: sketchModelId }]
  } as any;

  params = {
    ...artworkStore.sketch.params,
    ...params,
    source: 1,
    layoutReferId: artworkStore.sketch.params.layoutRefer?.id
  };
  //将一些东西塞进artworkStore进去先
  artworkStore.sketch.params.modelList = [data];

  return params;
}

// 开启模块生图
export async function startSketchGenerate(
  oldParams?: any,
  options?: {
    onClientIdGenerated?: (clientId: string) => void;
    onClientIdCleared?: () => void;
  }
) {
  const artworkStore = useArtworkStore();
  const modelStore = useModelStore();
  const params = await getSketchGenerateParam();

  const module = sketchParams.module;
  const modelList: any[] = params.modelList || [];

  const modelLabels = modelList.flatMap((item: any) => item.label || []);
  // 按照 modelLabels 中 ID 的顺序过滤和排序标签
  const modelTagList = modelLabels
    .map((labelId) => modelStore.labelOptions.find((item: any) => item.value === labelId))
    .filter(Boolean)
    .map((item) => item!.label);

  const modelCategories = modelList.map((item) => item.category).filter(Boolean);

  const modelTypeList = modelCategories
    .map((categoryId) => modelStore.categoryOptions.find((item: any) => item.value === categoryId))
    .filter(Boolean)
    .map((item) => item!.label);

  const isUserGeneratedText = (() => {
    if (modelList.every((i) => i.userId !== 0)) return "是";
    if (modelList.every((i) => i.userId === 0)) return "否";
    return "混合";
  })();

  const modelIdList = params.modelList.map((item: ModelState) => item.id);
  // 创建loraType映射关系
  const loraTypeMap = Object.fromEntries(styleModelOptions.map((option) => [option.value, option.label]));
  // 提取loraType对应的label组成数组
  const loraTypeLabels = modelList.map((item) => (item.loraType ? loraTypeMap[item.loraType] || "视频模型" : item.loraType));
  const modelNameList = modelList.map((i: any) => i.modelName) || [];

  startModuleGenerate(module, {
    getModuleParams: () => {
      return oldParams || params;
    },
    onStartTrack: (clientId) => {
      reportStartEventTracking(clientId);
    },
    onFinishTrack: (data) => {
      reportFinishEventTracking(data);
    },
    onClientIdGenerated: options?.onClientIdGenerated,
    onClientIdCleared: options?.onClientIdCleared
  });

  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {
    const eventValue: any = {
      client_id: clientId,
      module: getModuleLabelById(module) as string,
      source: params.source === 1 ? "直接生成" : "再迭代",
      artwork_id: artworkStore.artworkState.id.toString(),
      batchSize: params.batchSize,
      // model_id: params.modelList.map((item: ModelState) => item.id).join(","),
      hp_model_id: String(modelIdList.join(",")),
      model_name: params.modelList.map((item: ModelState) => item.modelName).join(",") || JSON.stringify(params.modelList || ["--"]),
      ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(","),
      lora_type: params.modelList.map((item: ModelState) => item.loraType).join(","),
      is_personal_model: getModelEventTrackType(params.modelList),
      model_tags: modelTagList.join(","),
      model_type: modelTypeList.join(","),

      hp_model_id_list: modelIdList,
      hp_model_name_list: modelNameList,
      hp_model_tags_list: modelTagList,
      hp_model_type_list: modelTypeList,
      hp_is_user_generated: isUserGeneratedText,
      is_composite_model: loraTypeLabels
    };

    // 高清修复
    if (gethdFix(params.hdFix, params.hdScale)) {
      eventValue.hd_fix = gethdFix(params.hdFix, params.hdScale);
    }

    useEventTracking({
      event: "creation_event",
      eventValue
    });
  }

  function reportFinishEventTracking({ client_id = "", result = 0 as 0 | 1, imageIds = "", queue_time_number = 0, loading_time_number = 0 } = {}) {
    const eventValue: any = {
      module: getModuleLabelById(module) as string,
      client_id,
      source: params.source === 1 ? "直接生成" : "再迭代",
      img_id: imageIds,
      result,
      queue_time_number,
      loading_time_number,
      // model_id: params.modelList.map((item: ModelState) => item.id).join(","),
      hp_model_id: String(modelIdList.join(",")),
      hp_model_id_list: modelIdList,
      model_name: params.modelList.map((item: ModelState) => item.modelName).join(",") || JSON.stringify(params.modelList || ["--"]),
      ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(","),
      lora_type: params.modelList.map((item: ModelState) => item.loraType).join(","),
      is_personal_model: getModelEventTrackType(params.modelList),
      model_source: mapModelSource(getModelEventTrackType(params.modelList)),
      model_id: String(artworkStore.artworkState.modelList.map((item: ModelState) => item.id).join(",")),
      model_tags: modelTagList.join(","),
      hp_model_tags_list: modelTagList,
      model_type: modelTypeList.join(","),
      hp_model_type_list: modelTypeList,
      hp_is_user_generated: isUserGeneratedText
    };
    // 高清修复
    if (gethdFix(params.hdFix, params.hdScale)) {
      eventValue.hd_fix = gethdFix(params.hdFix, params.hdScale);
    }
    useEventTracking({
      event: "artwork_status_event",
      eventValue
    });
  }
}
